import { useEffect, useState } from 'react';

interface PlansProps {
  onSelectPlan: (plan: string) => void;
}

interface PlanCardProps {
  name: string;
  slug: string;
  minutes: string;
  price: string;
  note: string;
  featured?: boolean;
  onSelect: () => void;
}

interface PlanFromAPI {
  id: string;
  name: string;
  duration: number;
  price: number;
  state: string;
  type: string;
  description?: string;
}

function PlanCard({ name, slug: _slug, minutes, price, note, featured, onSelect }: PlanCardProps) {
  return (
    <article className={`relative bg-[radial-gradient(circle_at_top_left,rgba(148,163,184,0.2),rgba(15,23,42,0.95))] rounded-[18px] p-5 border shadow-[0_16px_35px_rgba(15,23,42,0.7)] ${
      featured ? 'border-[#facc15] border-2' : 'border-[rgba(148,163,184,0.35)]'
    }`}>
      {featured && (
        <div className="absolute top-3 right-3 text-[0.7rem] uppercase tracking-wider bg-[#facc15] text-[#111827] px-2 py-1 rounded-full">
          Mais escolhido
        </div>
      )}
      <h3 className="mt-0 mb-1 text-lg font-bold text-[#e5e7eb]">{name}</h3>
      <p className="m-0 text-[#cbd5f5]">{minutes}</p>
      <p className="text-[1.6rem] font-bold my-2 text-[#facc15]">{price}</p>
      <p className="text-[0.9rem] text-[#e5e7eb] mb-4">{note}</p>
      <button
        onClick={onSelect}
        className="w-full inline-flex items-center justify-center rounded-full px-4 py-2 text-[0.95rem] font-medium bg-gradient-to-br from-[#8b5cf6] to-[#ec4899] text-[#f9fafb] shadow-[0_8px_20px_rgba(15,23,42,0.6)] hover:translate-y-[-1px] hover:brightness-105 transition-all"
      >
        Comprar {name}
      </button>
    </article>
  );
}

export default function Plans({ onSelectPlan }: PlansProps) {
  const [plans, setPlans] = useState<PlanCardProps[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const fetchPlans = async () => {
      try {
        const response = await fetch('/api/plans/active', { method: 'POST' });
        // console.log(console.log('Fetching plans from /api/plans');
        // console.log(console.log('Response status:', response.status);

        if (!response.ok) {
          throw new Error('Erro ao carregar planos');
        }
        // console.log(console.log('Response received from /api/plans');
        const payload = await response.json();
        // console.log(console.log('Payload:', payload);
        const plansData = Array.isArray(payload)
          ? payload
          : Array.isArray(payload?.plans)
            ? payload.plans
            : [];

        const transformedPlans = plansData
          .map((plan: PlanFromAPI) => ({
            name: plan.name,
            slug: plan.id,
            minutes: `${plan.duration} minutos`,
            price: `${(Number(plan.price) || 0).toFixed(2)} €`,
            note: plan.description?.trim()
              ? plan.description
              : (plan.type === 'IA' ? 'Consulta com inteligência artificial' : 'Consulta com especialista'),
            featured: plan.duration >= 20 && plan.type === 'IA',
          }));

        setPlans(transformedPlans);
      } catch (error) {
        console.error('Erro ao carregar planos:', error);
        // Fallback to empty if fetch fails
        setPlans([]);
      } finally {
        setIsLoading(false);
      }
    };

    fetchPlans();
  }, []);

  return (
    <section id="planos" className="py-[60px] bg-[radial-gradient(circle_at_top,rgba(15,23,42,0.9),#020617_55%)]">
      <div className="max-w-[1120px] mx-auto px-4">
        <h2 className="text-[1.8rem] mb-2 font-bold text-[#e5e7eb]">Escolha o seu pacote de minutos</h2>
        <p className="mt-0 mb-6 max-w-[640px] text-[#cbd5f5]">
          Compre minutos e fale já com o seu consultor virtual. Em média, cada resposta consome cerca de 1 minuto.
        </p>

        {isLoading ? (
          <div className="text-center text-[#cbd5f5]">A carregar planos...</div>
        ) : plans.length === 0 ? (
          <div className="text-center text-[#cbd5f5]">Nenhum plano disponível no momento.</div>
        ) : (
          <div className="grid grid-cols-[repeat(auto-fit,minmax(220px,1fr))] gap-6">
            {plans.map((plan) => (
              <PlanCard
                key={plan.slug}
                {...plan}
                onSelect={() => onSelectPlan(plan.slug)}
              />
            ))}
          </div>
        )}

        <p className="mt-4 text-[0.85rem] text-[#9ca3af] text-center">
          Pagamento seguro. O serviço é de entretenimento e não substitui aconselhamento profissional.
        </p>
      </div>
    </section>
  );
}
